import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserSelect, useWorkspaceMembers } from '@/entities/collab';
import {
  type Database,
  dbCan,
  isDatabaseOwner,
  useDeleteDatabase,
  useTransferDatabaseOwner,
  useUpdateDatabase,
} from '@/entities/database';
import { useWorkspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { Button, ConfirmDialog, Dialog, TextAreaField, TextField } from '@/shared/ui';

import styles from './DatabaseAdmin.module.css';

const NAME_MAX = 100;
const DESCRIPTION_MAX = 5000;

interface Props {
  database: Database;
  onClose: () => void;
  /** The database was deleted. */
  onDeleted: () => void;
}

function GeneralSection({ database }: { database: Database }) {
  const { t } = useTranslation();
  const update = useUpdateDatabase(database.id);
  const [name, setName] = useState(database.name);
  const [description, setDescription] = useState(database.description ?? '');
  const editable = dbCan(database, 'database.edit_details');
  const changed =
    name.trim() !== database.name || description.trim() !== (database.description ?? '');
  return (
    <section className={styles.section} aria-labelledby="db-general">
      <h3 id="db-general" className={styles.sectionTitle}>
        {t('databases.settings.general')}
      </h3>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim()) return;
          update.mutate({
            name: name.trim(),
            description: description.trim() || null,
            expected_version: database.version,
          });
        }}
      >
        <TextField
          label={t('databases.name')}
          value={name}
          maxLength={NAME_MAX}
          disabled={!editable}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <TextAreaField
          label={t('databases.description')}
          value={description}
          rows={3}
          maxLength={DESCRIPTION_MAX}
          disabled={!editable}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
        <FormError error={update.error} />
        {editable && (
          <div className={styles.actions}>
            <Button type="submit" variant="primary" disabled={!changed || update.isPending}>
              {t('common.save')}
            </Button>
          </div>
        )}
      </form>
    </section>
  );
}

function ConfidentialSection({
  database,
  workspaceOwner,
}: {
  database: Database;
  workspaceOwner: boolean;
}) {
  const { t } = useTranslation();
  const update = useUpdateDatabase(database.id);
  const [confirming, setConfirming] = useState(false);
  // On: the database owner or the workspace owner. Off: the workspace owner only.
  const mayChange = database.is_confidential ? workspaceOwner : isDatabaseOwner(database);
  return (
    <section className={styles.section} aria-labelledby="db-confidential">
      <h3 id="db-confidential" className={styles.sectionTitle}>
        {t('databases.confidential')}
      </h3>
      <p className={styles.text}>
        {database.is_confidential
          ? t('databases.settings.confidentialOn')
          : t('databases.settings.confidentialOff')}
      </p>
      {mayChange ? (
        <Button
          variant={database.is_confidential ? 'secondary' : 'primary'}
          disabled={update.isPending}
          onClick={() => {
            setConfirming(true);
          }}
        >
          {database.is_confidential
            ? t('databases.settings.makeNormal')
            : t('databases.settings.makeConfidential')}
        </Button>
      ) : (
        <p className={styles.note}>
          {database.is_confidential
            ? t('databases.settings.onlyWorkspaceOwnerOff')
            : t('databases.settings.onlyOwnersOn')}
        </p>
      )}
      <FormError error={update.error} />
      {confirming && (
        <ConfirmDialog
          title={
            database.is_confidential
              ? t('databases.settings.makeNormal')
              : t('databases.settings.makeConfidential')
          }
          message={
            database.is_confidential
              ? t('databases.settings.confirmNormal')
              : t('databases.settings.confirmConfidential')
          }
          confirmLabel={t('databases.settings.apply')}
          confirmVariant="primary"
          pending={update.isPending}
          onCancel={() => {
            setConfirming(false);
          }}
          onConfirm={() => {
            update.mutate(
              { is_confidential: !database.is_confidential, expected_version: database.version },
              {
                onSettled: () => {
                  setConfirming(false);
                },
              },
            );
          }}
        />
      )}
    </section>
  );
}

/** After a hand-over the viewer may have no access left: the page then says so. */
function OwnerSection({ database, onDone }: { database: Database; onDone: () => void }) {
  const { t } = useTranslation();
  const transfer = useTransferDatabaseOwner(database.id);
  const mayTransfer = isDatabaseOwner(database);
  const members = useWorkspaceMembers(database.workspace_id, mayTransfer);
  const [target, setTarget] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const targetName = members.data?.find((m) => m.user_id === target)?.display_name ?? '';
  return (
    <section className={styles.section} aria-labelledby="db-owner">
      <h3 id="db-owner" className={styles.sectionTitle}>
        {t('databases.settings.owner')}
      </h3>
      <p className={styles.text}>{t('databases.ownerName', { name: database.owner_name })}</p>
      {mayTransfer && (
        <div className={styles.inline}>
          <UserSelect
            label={t('databases.settings.newOwner')}
            placeholder={t('databases.settings.newOwner')}
            users={members.data ?? []}
            value={target}
            exclude={new Set([database.owner_user_id])}
            onChange={setTarget}
          />
          <Button
            variant="secondary"
            disabled={target === null}
            onClick={() => {
              setConfirming(true);
            }}
          >
            {t('databases.settings.transfer')}
          </Button>
        </div>
      )}
      <FormError error={transfer.error} />
      {confirming && target && (
        <ConfirmDialog
          title={t('databases.settings.transfer')}
          message={t('databases.settings.confirmTransfer', { name: targetName })}
          confirmLabel={t('databases.settings.transfer')}
          confirmVariant="primary"
          pending={transfer.isPending}
          onCancel={() => {
            setConfirming(false);
          }}
          onConfirm={() => {
            transfer.mutate(
              { user_id: target, expected_version: database.version },
              {
                onSettled: () => {
                  setConfirming(false);
                },
                onSuccess: onDone,
              },
            );
          }}
        />
      )}
    </section>
  );
}

/** Database-level settings, apart from the table data: details, mode, owner, deletion. */
export function DatabaseSettingsDialog({ database, onClose, onDeleted }: Props) {
  const { t } = useTranslation();
  const workspace = useWorkspace(database.workspace_id);
  const remove = useDeleteDatabase();
  const [deleting, setDeleting] = useState(false);
  return (
    <Dialog title={t('databases.settings.title')} size="lg" onClose={onClose}>
      <div className={styles.stack}>
        {database.content_visible && <GeneralSection database={database} />}
        <ConfidentialSection
          database={database}
          workspaceOwner={workspace.data?.is_owner ?? false}
        />
        <OwnerSection database={database} onDone={onClose} />
        {dbCan(database, 'database.delete') && (
          <section className={styles.section} aria-labelledby="db-delete">
            <h3 id="db-delete" className={styles.sectionTitle}>
              {t('databases.delete')}
            </h3>
            <p className={styles.text}>{t('databases.settings.deleteHint')}</p>
            <Button
              variant="danger"
              onClick={() => {
                setDeleting(true);
              }}
            >
              {t('databases.delete')}
            </Button>
          </section>
        )}
      </div>
      {deleting && (
        <ConfirmDialog
          title={t('databases.deleteTitle')}
          message={t('databases.deleteMessage', { name: database.name })}
          confirmLabel={t('databases.delete')}
          pending={remove.isPending}
          onCancel={() => {
            setDeleting(false);
          }}
          onConfirm={() => {
            remove.mutate(database, { onSuccess: onDeleted });
          }}
        />
      )}
    </Dialog>
  );
}
